import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import i18n from '@/i18n'

type Mode = 'light' | 'dark'
type ColorTheme = 'default' | 'midnight' | 'ocean'
type Language = 'th' | 'en' | 'lo' | 'km'

interface ThemeState {
  mode: Mode
  colorTheme: ColorTheme
  language: Language
  setMode: (mode: Mode) => void
  toggleMode: () => void
  setColorTheme: (theme: ColorTheme) => void
  setLanguage: (language: Language) => void
}

const applyTheme = (mode: Mode, colorTheme: ColorTheme) => {
  const root = document.documentElement
  
  // Disable transitions temporarily
  root.classList.add('no-transitions')
  
  // Apply mode
  if (mode === 'dark') {
    root.classList.add('dark')
    root.classList.remove('light')
  } else {
    root.classList.add('light')
    root.classList.remove('dark')
  }
  
  // Apply color theme
  root.classList.remove('theme-default', 'theme-midnight', 'theme-ocean')
  if (colorTheme !== 'default') {
    root.classList.add(`theme-${colorTheme}`)
  }
  
  // Re-enable transitions after a brief delay
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.classList.remove('no-transitions')
    })
  })
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'dark',
      colorTheme: 'default',
      language: 'th',
      
      setMode: (mode) => {
        set({ mode })
        applyTheme(mode, get().colorTheme)
      },
      
      toggleMode: () => {
        const newMode = get().mode === 'dark' ? 'light' : 'dark'
        get().setMode(newMode)
      },
      
      setColorTheme: (colorTheme) => {
        set({ colorTheme })
        applyTheme(get().mode, colorTheme)
      },
      
      setLanguage: (language) => {
        set({ language })
        i18n.changeLanguage(language)
        // Update HTML lang attribute for font switching
        document.documentElement.lang = language
        document.documentElement.className = document.documentElement.className
          .replace(/lang-\w+/g, '')
          .trim() + ` lang-${language}`
      },
    }),
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Apply saved theme on load
          setTimeout(() => {
            applyTheme(state.mode, state.colorTheme)
          }, 0)
          // Apply saved language and font
          if (state.language) {
            i18n.changeLanguage(state.language)
            document.documentElement.lang = state.language
            document.documentElement.classList.add(`lang-${state.language}`)
          }
        }
      },
    }
  )
)

// Language configs
export const LANGUAGES: Record<Language, { label: string; flag: string }> = {
  th: { label: 'ไทย', flag: '🇹🇭' },
  en: { label: 'English', flag: '🇬🇧' },
  lo: { label: 'ລາວ', flag: '🇱🇦' },
  km: { label: 'ខ្មែរ', flag: '🇰🇭' },
}

// Theme configs
export const THEMES: Record<ColorTheme, { 
  label: string
  labelTh: string
  description: string
  descriptionTh: string
  icon: string
}> = {
  default: { 
    label: 'Default',
    labelTh: 'มาตรฐาน',
    description: 'Clean Zinc aesthetic',
    descriptionTh: 'สะอาด เรียบง่าย',
    icon: '⚡',
  },
  midnight: { 
    label: 'Midnight',
    labelTh: 'มิดไนท์',
    description: 'Professional, Bloomberg-style',
    descriptionTh: 'มืออาชีพ สไตล์บลูมเบิร์ก',
    icon: '🌃',
  },
  ocean: { 
    label: 'Ocean',
    labelTh: 'โอเชี่ยน',
    description: 'Futuristic, Cyberpunk-style',
    descriptionTh: 'ล้ำสมัย สไตล์ไซเบอร์พังค์',
    icon: '🌊',
  },
}
